import { ROLL_30336, SHEET_30UP, type LabelStock } from './layout.js';

/**
 * Where labels go when "Print all labels" is pressed, and which of them this
 * browser uses.
 *
 * A destination is chosen **per browser**, not per account: the computer by the
 * packing bench prints to a label roll, the one in the office to sheets on a
 * network printer, and the same person signs in on both. So the choice lives in
 * localStorage (beside the theme and the navigation state), never on the server.
 *
 * Two destinations produce a PDF, which the browser's own print dialog prints.
 * The third prints straight to a DYMO LabelWriter through DYMO Connect, with no
 * dialog. A destination has a `kind`. A PDF is always available. Whether the
 * DYMO printer is comes from ONE place, the shared DYMO status
 * (dymo-browser.ts, read through `dymoAvailability` in useLabelDestination.ts
 * by the picker and the Labeling page, and checked again by `printToDymo` at
 * print time): while it is not available it is listed but cannot be chosen, and
 * a stored choice of it uses the default until it is available again.
 * Everything that prints goes through `printLabels` (print.ts).
 */

/**
 * How a destination delivers the job:
 *   - `pdf`: one PDF document for the whole list, opened for the browser to print;
 *   - `dymo`: one print job sent to a DYMO LabelWriter through DYMO Connect's
 *     local service, with no print dialog (dymo-status.ts).
 */
export type LabelDestinationKind = 'pdf' | 'dymo';

export type LabelDestinationId = 'sheet-30up' | 'roll-30336' | 'dymo-30336';

/** The direct DYMO destination's id, for the places that treat it specially. */
export const DYMO_DESTINATION_ID: LabelDestinationId = 'dymo-30336';

/** Whether a destination can be used on this computer right now. */
export interface DestinationAvailability {
  available: boolean;
  /** Why not, in a sentence for the settings page; null when available. */
  reason: string | null;
}

export interface LabelDestination {
  id: LabelDestinationId;
  kind: LabelDestinationKind;
  /** The name in the picker. */
  name: string;
  /** One line under the name: the stock and what happens on print. */
  summary: string;
  stock: LabelStock;
  /** Whether "start at label N" applies (a sheet can be partly used; a roll cannot). */
  supportsStartAt: boolean;
  /**
   * Asks whether a PDF destination can be used on this computer. Absent means
   * always available, which every destination here is today. The DYMO printer
   * deliberately has none: its availability is the shared DYMO status, so there
   * is one answer and one probe, not two (see the note at the top).
   */
  detect?: () => Promise<DestinationAvailability>;
}

export const ALWAYS_AVAILABLE: DestinationAvailability = { available: true, reason: null };

/** Every destination, in the order the picker lists them. */
export const LABEL_DESTINATIONS: readonly LabelDestination[] = [
  {
    id: 'sheet-30up',
    kind: 'pdf',
    name: 'Sheet labels, 30 per sheet',
    summary: 'US Letter sheets of 30 labels, 1 x 2-5/8 in (Avery 5160 layout). Opens a PDF.',
    stock: SHEET_30UP,
    supportsStartAt: true,
  },
  {
    id: 'roll-30336',
    kind: 'pdf',
    name: 'Label roll 1 x 2-1/8 in (Dymo 30336)',
    summary: 'One label per page at the label’s own size. Opens a PDF.',
    stock: ROLL_30336,
    supportsStartAt: false,
  },
  {
    id: 'dymo-30336',
    kind: 'dymo',
    name: 'DYMO LabelWriter, no print dialog',
    summary:
      'Prints straight to the DYMO LabelWriter on a 30336 roll (1 x 2-1/8 in) through DYMO Connect. Needs DYMO Connect on this computer.',
    stock: ROLL_30336,
    supportsStartAt: false,
  },
];

/**
 * The destination used when nothing is saved, and the fallback when the saved
 * one cannot be used: the sheet PDF, which needs no particular printer.
 */
export const DEFAULT_DESTINATION_ID: LabelDestinationId = 'sheet-30up';

export function destinationById(id: LabelDestinationId): LabelDestination {
  return LABEL_DESTINATIONS.find((d) => d.id === id) ?? (LABEL_DESTINATIONS[0] as LabelDestination);
}

/** Where this browser's choice is kept. */
export const DESTINATION_STORAGE_KEY = 'seller.labels.destination';

/** The stored choice as a destination id; anything unknown is the default. */
export function parseDestinationId(stored: string | null): LabelDestinationId {
  return LABEL_DESTINATIONS.some((d) => d.id === stored)
    ? (stored as LabelDestinationId)
    : DEFAULT_DESTINATION_ID;
}

/** Whether one destination can be used on this computer right now. */
export async function detectDestination(
  destination: LabelDestination,
): Promise<DestinationAvailability> {
  if (!destination.detect) return ALWAYS_AVAILABLE;
  try {
    return await destination.detect();
  } catch {
    return { available: false, reason: 'The printer could not be checked.' };
  }
}

/**
 * The destination a print actually uses: the chosen one when it is available,
 * otherwise the default (which always is). The caller is told when it fell
 * back, so the page can say so instead of printing somewhere unexpected.
 */
export async function resolveDestination(
  chosen: LabelDestinationId,
): Promise<{ destination: LabelDestination; fellBack: boolean; reason: string | null }> {
  const destination = destinationById(chosen);
  const availability = await detectDestination(destination);
  if (availability.available) return { destination, fellBack: false, reason: null };
  return {
    destination: destinationById(DEFAULT_DESTINATION_ID),
    fellBack: true,
    reason: availability.reason,
  };
}
